import { useState } from 'react';
import {
  DEFAULT_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
  type UsePaginationReturn,
} from '@/shared/types/pagination';
import { computeVisiblePages } from '@/shared/utils/paginationUtils';

interface UsePaginationOptions {
  totalItems: number;
  initialPageSize?: number;
  initialPageIndex?: number;
  // Controlled mode: when provided, the hook uses external state instead of internal useState
  pageIndex?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
}

export function usePagination({
  totalItems,
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialPageIndex = 0,
  pageIndex: controlledPageIndex,
  onPageChange,
  pageSize: controlledPageSize,
  onPageSizeChange,
}: UsePaginationOptions): UsePaginationReturn {
  const [internalPageIndex, setInternalPageIndex] = useState(initialPageIndex);
  const [internalPageSize, setInternalPageSize] = useState(
    PAGE_SIZE_OPTIONS.includes(initialPageSize) ? initialPageSize : DEFAULT_PAGE_SIZE,
  );

  const isControlled = controlledPageIndex !== undefined && onPageChange !== undefined;
  const isPageSizeControlled = controlledPageSize !== undefined && onPageSizeChange !== undefined;

  const pageIndex = isControlled ? controlledPageIndex : internalPageIndex;
  const pageSize = isPageSizeControlled ? controlledPageSize : internalPageSize;

  const setPageIndex = isControlled ? onPageChange : setInternalPageIndex;
  const setPageSize = isPageSizeControlled ? onPageSizeChange : setInternalPageSize;

  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0;

  const canPrevious = pageIndex > 0;
  const canNext = totalPages > 0 && pageIndex < totalPages - 1;

  const visiblePages = computeVisiblePages(pageIndex, totalPages);

  const goToPage = (page: number) => {
    if (totalPages === 0) return;
    const clamped = Math.max(0, Math.min(page, totalPages - 1));
    setPageIndex(clamped);
  };

  const next = () => {
    setPageIndex(totalPages > 0 ? Math.min(pageIndex + 1, totalPages - 1) : pageIndex);
  };

  const previous = () => {
    setPageIndex(Math.max(pageIndex - 1, 0));
  };

  const first = () => {
    setPageIndex(0);
  };

  const last = () => {
    if (totalPages > 0) setPageIndex(totalPages - 1);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPageIndex(0);
  };

  return {
    pageIndex,
    pageSize,
    totalItems,
    totalPages,
    visiblePages,
    canPrevious,
    canNext,
    goToPage,
    next,
    previous,
    first,
    last,
    changePageSize,
  };
}
